import 'reactjs-popup/dist/index.css';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { adjustShadingInit, undoRemoveShading } from '../ButtonDiv/api';
import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { toggleShadingModalState } from 'store/slices/EditFacetModalSlice';
import { setEditShadingPerfomed, setRoofIndexes } from 'store/slices/ToolSlice';
import { useRefs } from 'contexts/RefContext';
import { resetAllTransformations } from 'components/tool/utils';
import { appToast } from '../../../Basic/CustomToast';
import ConfirmationModal from 'components/Basic/Modal/Confirmation';
import { openShadingMenu } from 'store/slices/AdjustShadingSlice';
import { EnergyState } from 'store/slices/EnergySlice/types';
import { getDeratedValue } from '../../../../util';

export default function ShadingConfirmationModal() {
	const dispatch = useDispatch<AppDispatch>();
	const { shadingModal: { open, type: actionType } } = useSelector((state: RootState) => state.editFacetModal.data);
	const { uuid, panel, allRoofSegs, roofSegsFromWhichUserHasSelectedPanels, designType } =
		useSelector((state: RootState) => state.roofData.data);
	const { selectedRoofIndex, roofIndexes } = useSelector((state: RootState) => state.toolNewPostions.data);
	const someFacetIsStillInEditMode = !!roofSegsFromWhichUserHasSelectedPanels.filter(ind => roofIndexes.includes(ind)).length;
	const { konvaRef } = useRefs();
	const msg = `Are you sure you want to ${actionType === 'undo' ? 'undo remove' : 'adjust'} shading of the facet?`;
	const facet = allRoofSegs.find(rs => rs.id === selectedRoofIndex);

	const { selectedPanels: selectedPanelsOnUI = [], deratedBy } = useSelector(
		(state: RootState) => (state.energyData.data ?? {}) as EnergyState
	);
	const { totalSelectedPanelsFromFacet, totalSelectedPanelsProduction } = (facet?.panels ?? []).reduce((sum, panel) => ({
		totalSelectedPanelsFromFacet: sum.totalSelectedPanelsFromFacet + (selectedPanelsOnUI.includes(panel.id) ? 1 : 0),
		totalSelectedPanelsProduction: sum.totalSelectedPanelsProduction + (selectedPanelsOnUI.includes(panel.id) ? panel.yearlyEnergyDcKwh : 0)
	}), { totalSelectedPanelsFromFacet: 0, totalSelectedPanelsProduction: 0 });
	const { totalPanelsFromFacet, totalPanelsProduction } = (facet?.panels ?? []).reduce((sum, panel) => ({
		totalPanelsFromFacet: sum.totalPanelsFromFacet + 1,
		totalPanelsProduction: sum.totalPanelsProduction + panel.yearlyEnergyDcKwh
	}), { totalPanelsFromFacet: 0, totalPanelsProduction: 0 });
	const azimuth = facet?.azimuthDegrees || 0;
	const pitch = facet?.pitchDegrees || 0;

	function resetTranslaedFacets() {
		dispatch(setRoofIndexes({ roofIndexes: [] }));
		if (konvaRef?.current) {
			resetAllTransformations(konvaRef.current);
		}
	}

	const removeShadingQuery = useMutation( async () => {
		if (!facet) return;
		const payload = {
			facetId: selectedRoofIndex as string,
			panelKey: panel.key,
			isUserMapped: !!facet.userMapped,
			shading: Math.round(facet?.shading || 0),
		};
		const request$ = actionType === 'undo' ? undoRemoveShading : adjustShadingInit;
		if(actionType === 'adjust'){
			dispatch(openShadingMenu(
				{
					azimuth: azimuth,
					pitch: pitch,
					systemProduction: getDeratedValue(totalSelectedPanelsProduction, deratedBy),
					panelsCount: totalSelectedPanelsFromFacet,
					systemSize: totalSelectedPanelsFromFacet * panel.wattage,
					segmentId: selectedRoofIndex!, 
					shadingVal: facet?.shading ?? 0, 
					isUserMapped: !!facet?.userMapped, 
					panelKey: panel.key,
					isShadingAdjusted: !!facet?.isShadingAdjusted,
					doneEnabled: false,
					totalPanelsProduction: getDeratedValue(totalPanelsProduction, deratedBy),
					totalSystemSize: totalPanelsFromFacet * panel.wattage,
					totalPanelsFromFacet: totalPanelsFromFacet,
				}
			));
			return;
		}
		const reponse = await request$(uuid, payload);
		dispatch(setEditShadingPerfomed({editShadingPerformed: true}));
		await dispatch(changePanel({ uuid, panelKey: panel.key, designType:designType }));
		dispatch(setEditShadingPerfomed({editShadingPerformed: false}));
		return {shading: reponse.data.shading, isUserMapped: !!facet?.userMapped, isShadingAdjusted: !!facet?.isShadingAdjusted};
	},
	{
		mutationKey: 'remove-shading',
		onSuccess(reponse) {
			//tranlate with remove shading: cannot perform together for auto-generated because of mismatch of facet ID
			if (someFacetIsStillInEditMode) {
				resetTranslaedFacets();
			}
			if(!reponse)	return;
			const {shading, isUserMapped, isShadingAdjusted} = reponse;
			if(actionType === 'init'){
				dispatch(openShadingMenu({
					azimuth: azimuth,
					pitch: pitch,
					systemProduction: getDeratedValue(totalSelectedPanelsProduction, deratedBy),
					panelsCount: totalSelectedPanelsFromFacet,
					systemSize: totalSelectedPanelsFromFacet * panel.wattage,
					segmentId: selectedRoofIndex!,
					shadingVal: shading, 
					isUserMapped: isUserMapped,
					panelKey: panel.key,
					isShadingAdjusted: isShadingAdjusted,
					doneEnabled: false,
					totalPanelsProduction: getDeratedValue(totalPanelsProduction, deratedBy),
					totalSystemSize: totalPanelsFromFacet * panel.wattage,
					totalPanelsFromFacet: totalPanelsFromFacet,
				}));
			}
		},
		onError(error: AxiosError<{ message: string }>) {
			if (error.response?.data) {
				appToast.error(error.response.data?.message || error.message);
			}
		}
	});

	function closeModal() {
		dispatch(toggleShadingModalState({ value: false, type: actionType }));
	}

	function hanldeConfirmation() {
		closeModal();
		removeShadingQuery.mutate();
	}

	return (
		<ConfirmationModal
			open={open}
			onConfirm={hanldeConfirmation}
			onCancel={()=>closeModal()}
			message={msg}
		/>
	);
}

